// src/pages/nba/NbaCheatSheet.jsx
import React, { useEffect, useMemo, useState } from "react";

/* -------------------------------------------------------------
   Config
-------------------------------------------------------------- */

const CHEAT_URL =
  import.meta.env?.VITE_NBA_CHEAT_SHEET_URL ||
  "/data/nba/latest/cheat_sheets.json";

const NBA_LOGO_BASE = "/logos/nba";

/* Desired column order when present */
const CANON_ORDER = [
  "PLAYER",
  "SALARY",
  "TEAM",
  "MATCHUP",
  "VEGAS",
  "TEAM TOTAL",
  "PACE",
  "PROJ",
  "VALUE",
];

/* Aliases -> canonical column names */
function canon(h) {
  if (!h) return "";
  const s = String(h).trim().toLowerCase();
  const map = {
    pg: "PLAYER",
    sg: "PLAYER",
    sf: "PLAYER",
    pf: "PLAYER",
    c: "PLAYER",
    "cash core": "PLAYER",
    player: "PLAYER",
    players: "PLAYER",
    salary: "SALARY",
    team: "TEAM",
    matchup: "MATCHUP",
    vs: "MATCHUP",
    "o/u": "VEGAS",
    vegas: "VEGAS",
    "team total": "TEAM TOTAL",
    "imp. total": "TEAM TOTAL",
    pace: "PACE",
    proj: "PROJ",
    projection: "PROJ",
    value: "VALUE",
  };
  return map[s] || h.toString().toUpperCase();
}

/* Team abbrev normalizer so logos resolve */
const TEAM_ABBR_MAP = {
  NOR: "NOP",
  NO: "NOP",
  NOH: "NOP",
  CHO: "CHA",
  PHO: "PHX",
  UTH: "UTA",
  NY: "NYK",
  NJN: "BKN",
  BRK: "BKN",
  GSW: "GSW",
  SAS: "SAS",
};

function normTeam(abbr) {
  if (!abbr) return "";
  const t = String(abbr).trim().toUpperCase();
  return TEAM_ABBR_MAP[t] || t;
}

function logoUrl(team) {
  const t = normTeam(team);
  if (!t) return "";
  return `${NBA_LOGO_BASE}/${t}.png`;
}

/* Build display columns (canonical names) */
function computeColumns(table) {
  let cols = Array.isArray(table?.columns) ? [...table.columns] : [];
  if (!cols.length && table?.rows?.length) cols = Object.keys(table.rows[0]);
  const normalized = cols.map(canon);

  const haveAnyCanon = normalized.some((c) => CANON_ORDER.includes(c));
  if (haveAnyCanon) {
    const set = new Set(normalized);
    const ordered = CANON_ORDER.filter((c) => set.has(c));
    const extras = normalized.filter((c) => !ordered.includes(c));
    return [...ordered, ...extras];
  }
  return normalized;
}

/* Formatting helpers */
function fmtNumber1(x) {
  const n = Number(x);
  if (!Number.isFinite(n)) return x ?? "";
  return n.toFixed(1);
}

function fmtSalary(x) {
  if (x == null || x === "") return "";
  const n = Number(String(x).replace(/[$,]/g, ""));
  if (!Number.isFinite(n)) return String(x);
  return `$${Math.round(n).toLocaleString()}`;
}

const ONE_DEC_COLS = new Set(["VEGAS", "TEAM TOTAL", "PACE", "PROJ", "VALUE"]);

/* Preferred order for tables */
const TABLE_ORDER = ["PG", "SG", "SF", "PF", "C", "Cash Core"];

/* Try to find a timestamp in various shapes of JSON */
function findUpdated(obj) {
  if (!obj || typeof obj !== "object") return null;
  const keys = [
    "updated_at",
    "updated",
    "last_updated",
    "generated_at",
    "timestamp",
  ];
  for (const k of keys) {
    if (obj[k]) return obj[k];
  }
  if (Array.isArray(obj.artifacts)) {
    for (const art of obj.artifacts) {
      if (art.last_updated) return art.last_updated;
      if (art.updated_at) return art.updated_at;
      if (art.tags?.last_updated) return art.tags.last_updated;
    }
  }
  return null;
}

/* -------------------------------------------------------------
   UI bits
-------------------------------------------------------------- */

function Box({ title, children, isDark }) {
  const cardBase =
    "rounded-2xl border shadow-[0_16px_40px_rgba(15,23,42,0.18)] overflow-hidden w-full min-w-0";

  const cardClass = isDark
    ? `${cardBase} border-slate-800 bg-slate-950`
    : `${cardBase} border-slate-200 bg-white`;

  // Keep blue bar for both themes
  const headerClass =
    "bg-sky-700 text-white border-b border-sky-800 px-3 py-2 text-sm font-semibold";

  return (
    <div className={cardClass}>
      <div className={headerClass}>{title}</div>
      {children}
    </div>
  );
}

/* Cell renderer: logo + team + player, plus numeric formatting */
function Cell({ col, row }) {
  const vRaw = row[col];

  if (col === "PLAYER") {
    const team = normTeam(row["TEAM"]);
    const url = logoUrl(team);
    return (
      <div className="flex min-w-0 items-center gap-1.5">
        {team ? (
          <img
            src={url}
            alt={team}
            className="h-4 w-4 shrink-0 object-contain"
            onError={(e) => (e.currentTarget.style.display = "none")}
          />
        ) : null}
        <div className="min-w-0 leading-tight">
          <div className="flex min-w-0 flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
            {team ? (
              <span className="font-semibold tracking-wide">
                {team}
              </span>
            ) : null}
            <span className="min-w-0 [overflow-wrap:anywhere]">{String(vRaw ?? "")}</span>
          </div>
        </div>
      </div>
    );
  }

  if (col === "SALARY") {
    return <span className="tabular-nums">{fmtSalary(vRaw)}</span>;
  }

  if (ONE_DEC_COLS.has(col)) {
    return <span className="tabular-nums">{fmtNumber1(vRaw)}</span>;
  }

  return <span>{String(vRaw ?? "")}</span>;
}

/* Compact table styled like NFL Player Pool tables */
function ThinTable({ columns, rows, isDark }) {
  const rowOdd = isDark ? "bg-slate-950" : "bg-white";
  const rowEven = isDark ? "bg-slate-900/85" : "bg-gray-50";

  const headBg = isDark
    ? "bg-slate-900/95 text-slate-100 border-slate-800"
    : "bg-slate-100 text-slate-700 border-slate-200";

  const columnWeight = (column) =>
    column === "PLAYER" ? 3.6 : column === "MATCHUP" ? 1.4 : 1;
  const totalColumnWeight = columns.reduce(
    (sum, column) => sum + columnWeight(column),
    0
  );

  return (
    <div className="w-full min-w-0">
      <table
        className={`w-full table-fixed border-separate text-sm ${
          isDark ? "text-slate-100" : "text-slate-900"
        }`}
        style={{ borderSpacing: 0 }}
      >
        <colgroup>
          {columns.map((c) => (
            <col
              key={c}
              style={{
                width: `${(columnWeight(c) / Math.max(totalColumnWeight, 1)) * 100}%`,
              }}
            />
          ))}
        </colgroup>
        <thead className={headBg}>
          <tr>
            {columns.map((c) => (
              <th
                key={c}
                className="px-1 py-2 text-center font-semibold text-[10px] uppercase leading-tight [overflow-wrap:anywhere] border-b"
              >
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td
                className="px-3 py-3 text-center text-slate-500"
                colSpan={columns.length}
              >
                No rows.
              </td>
            </tr>
          ) : (
            rows.map((r, i) => (
              <tr key={i} className={i % 2 === 0 ? rowOdd : rowEven}>
                {columns.map((c) => (
                  <td
                    key={c}
                    className={`px-1 py-2 text-[11px] leading-tight [overflow-wrap:anywhere] align-middle ${c === "PLAYER" ? "text-left" : "text-center"}`}
                  >
                    <Cell col={c} row={r} />
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

/* -------------------------------------------------------------
   Page
-------------------------------------------------------------- */

export default function NbaCheatSheet() {
  const [tables, setTables] = useState([]);
  const [error, setError] = useState("");
  const [updatedStamp, setUpdatedStamp] = useState("");
  const [theme, setTheme] = useState("dark");

  const isDark = theme === "dark";

  useEffect(() => {
    const url = `${CHEAT_URL}?v=${Date.now()}`;
    (async () => {
      try {
        setError("");
        const res = await fetch(url, { cache: "no-store" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        // Try to find "updated" info in several places
        const rawUpdated =
          findUpdated(data) ||
          findUpdated(data.meta || {}) ||
          findUpdated(data.tags || {});
        if (rawUpdated) {
          const d = new Date(rawUpdated);
          if (!Number.isNaN(d.getTime())) {
            setUpdatedStamp(
              d.toLocaleString(undefined, {
                month: "numeric",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
              })
            );
          }
        }

        if (!data?.tables?.length) {
          setError("No tables found in cheat_sheets.json");
          setTables([]);
          return;
        }

        const normed = data.tables.map((t) => {
          const cols = computeColumns(t);
          const rows =
            t.rows?.map((row) => {
              const out = {};
              Object.entries(row || {}).forEach(([k, v]) => {
                out[canon(k)] = v;
              });
              return out;
            }) ?? [];
          return { label: t.label || t.id || "Table", columns: cols, rows };
        });

        setTables(normed);
      } catch (e) {
        setError(String(e.message || e));
        setTables([]);
      }
    })();
  }, []);

  // Sort tables into PG, SG, SF, PF, C, Cash Core order when possible
  const orderedTables = useMemo(() => {
    return [...tables].sort((a, b) => {
      const la = String(a.label || "").toLowerCase();
      const lb = String(b.label || "").toLowerCase();

      const idxA = TABLE_ORDER.findIndex((x) =>
        la.startsWith(x.toLowerCase())
      );
      const idxB = TABLE_ORDER.findIndex((x) =>
        lb.startsWith(x.toLowerCase())
      );

      const na = idxA === -1 ? 999 : idxA;
      const nb = idxB === -1 ? 999 : idxB;
      return na - nb;
    });
  }, [tables]);

  const outerClass = isDark
    ? "min-h-screen bg-slate-950 text-slate-100"
    : "min-h-screen bg-slate-100 text-slate-900";

  return (
    <div className={outerClass}>
      <div className="mx-auto max-w-[1800px] px-3 sm:px-5 lg:px-6 py-5 md:py-6">
        {/* Header bar */}
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <h1 className="text-2xl font-extrabold tracking-tight md:text-3xl">
                NBA — Cheat Sheet
              </h1>
              <span
                className={`text-xs italic ${
                  isDark ? "text-slate-400" : "text-slate-500"
                }`}
              >
                Updated: {updatedStamp || "—"}
              </span>
            </div>
          </div>

          {/* Theme toggle */}
          <div className="flex items-center gap-2 self-start sm:self-auto">
            <span className="text-xs text-slate-400">Theme</span>
            <div className="inline-flex rounded-full bg-slate-900/70 p-1 border border-slate-700/70">
              {["dark", "light"].map((t) => {
                const active = theme === t;
                return (
                  <button
                    key={t}
                    onClick={() => setTheme(t)}
                    className={
                      "px-2.5 py-1 rounded-full text-xs transition " +
                      (active
                        ? "bg-sky-500 text-slate-950 shadow"
                        : "text-slate-300 hover:text-white")
                    }
                  >
                    {t === "dark" ? "Dark" : "Light"}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {error && (
          <div className="text-red-400 font-semibold mb-4">
            Failed to load: {error}
          </div>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-2 items-start gap-4 lg:gap-5">
          {orderedTables.map((t) => (
            <Box key={t.label} title={t.label} isDark={isDark}>
              <ThinTable
                columns={t.columns || []}
                rows={t.rows || []}
                isDark={isDark}
              />
            </Box>
          ))}
        </div>
      </div>
    </div>
  );
}
